import React, { useState, useEffect } from 'react';
import { AICharacter, AICharacterFormData } from '../../services/aiCharacterService';
import './AICharacterForm.css';

interface AICharacterFormProps {
  character?: AICharacter;
  onSubmit: (data: AICharacterFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const AICharacterForm: React.FC<AICharacterFormProps> = ({ character, onSubmit, onCancel, loading = false }) => {
  const [formData, setFormData] = useState<AICharacterFormData>({
    name: '',
    model_provider: 'claude',
    model_name: '',
    persona_info: { description: '' },
    is_active: true
  });
  const [personaDescription, setPersonaDescription] = useState<string>('');
  const [enabledTools, setEnabledTools] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Available tools for selection
  const availableTools = [
    // Novels
    { name: 'get_user_novels', label: 'Get User Novels' },
    { name: 'get_novel', label: 'Get Novel Details' },
    { name: 'get_chapter', label: 'Get Chapter Content' },
    { name: 'update_novel', label: 'Update Novel' },
    { name: 'update_novel_metadata', label: 'Update Novel Metadata' },
    // Cashflow
    { name: 'get_cashflow_entries', label: 'Get Cashflow Entries' },
    { name: 'get_cashflow_summary', label: 'Get Cashflow Summary' },
    { name: 'get_cashflow_categories', label: 'Get Cashflow Categories' },
    // Tasks
    { name: 'create_task', label: 'Create Task' },
    { name: 'get_user_tasks', label: 'Get User Tasks' },
    { name: 'get_task_by_id', label: 'Get Task Details' },
    { name: 'get_tasks_due_today', label: 'Get Tasks Due Today' },
    { name: 'get_tasks_assigned_for_date', label: 'Get Tasks for Date' },
    { name: 'get_task_stats', label: 'Get Task Statistics' },
    // Habits
    { name: 'get_user_habits', label: 'Get User Habits' },
    { name: 'get_habit_by_id', label: 'Get Habit Details' },
    { name: 'get_daily_completions', label: 'Get Daily Completions' },
    { name: 'get_habit_stats', label: 'Get Habit Statistics' },
    { name: 'get_habit_streak', label: 'Get Habit Streak' },
    // Goals
    { name: 'get_user_goals', label: 'Get User Goals' },
    { name: 'get_goal_by_id', label: 'Get Goal Details' },
    { name: 'get_goal_progress_history', label: 'Get Goal Progress History' },
    // Knowledge
    { name: 'get_knowledge_categories', label: 'Get Knowledge Categories' },
    { name: 'get_knowledge_category', label: 'Get Knowledge Category' },
    { name: 'get_lessons', label: 'Get Lessons' },
    { name: 'get_lesson', label: 'Get Lesson' },
    // Notes
    { name: 'get_daily_notes', label: 'Get Daily Notes' }
  ];

  // Model suggestions based on provider
  const modelSuggestions: Record<string, string[]> = {
    claude: ['claude-sonnet-4-5', 'claude-haiku-4-5'],
    gemini: ['gemini-pro', 'gemini-2.5-pro', 'gemini-2.5-flash'],
    gpt: ['gpt-4', 'gpt-4-turbo', 'gpt-4o', 'gpt-3.5-turbo', 'gpt-4o-mini'],
    grok: ['grok-4-1-fast-non-reasoning-latest']
  };

  useEffect(() => {
    if (character) {
      setFormData({
        name: character.name,
        model_provider: character.model_provider,
        model_name: character.model_name,
        persona_info: character.persona_info,
        is_active: character.is_active
      });
      // Extract description from persona_info
      if (character.persona_info && typeof character.persona_info === 'object') {
        setPersonaDescription(character.persona_info.description || '');
        // Extract enabled tools from persona_info
        setEnabledTools(character.persona_info.enabled_tools || []);
      } else {
        setPersonaDescription('');
        setEnabledTools([]);
      }
    } else {
      setPersonaDescription('');
      setEnabledTools([]);
    }
  }, [character]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'is_active') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else if (name === 'model_provider') {
      // Reset model_name when provider changes
      // Clear enabled tools if switching away from Claude (tools only work with Claude)
      const newProvider = value as 'claude' | 'gemini' | 'gpt' | 'grok';
      setFormData(prev => ({ 
        ...prev, 
        model_provider: newProvider, 
        model_name: '' 
      }));
      if (newProvider !== 'claude') {
        setEnabledTools([]);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!formData.model_name.trim()) {
      setError('Model name is required.');
      return;
    }

    // Use persona description from the textarea and include enabled tools
    const personaInfo: any = { description: personaDescription.trim() };
    // Always set enabled_tools (empty array if none selected) to ensure it's updated
    personaInfo.enabled_tools = enabledTools.length > 0 ? enabledTools : [];
    
    const submitData: AICharacterFormData = {
      ...formData,
      persona_info: personaInfo,
      enabled_tools: enabledTools.length > 0 ? enabledTools : undefined
    };

    try {
      await onSubmit(submitData);
    } catch (err) {
      setError('Failed to save character.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="ai-character-form">
      {error && <div className="ai-character-form-error">{error}</div>}
      
      <div className="ai-character-form-group">
        <label htmlFor="name">Name *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="ai-character-form-input"
        />
      </div>

      <div className="ai-character-form-group">
        <label htmlFor="model_provider">Model Provider *</label>
        <select
          id="model_provider"
          name="model_provider"
          value={formData.model_provider}
          onChange={handleChange}
          required
          className="ai-character-form-select"
        >
          <option value="claude">Claude</option>
          <option value="gemini">Gemini</option>
          <option value="gpt">GPT</option>
          <option value="grok">Grok</option>
        </select>
      </div>

      <div className="ai-character-form-group">
        <label htmlFor="model_name">Model Name *</label>
        <input
          type="text"
          id="model_name"
          name="model_name"
          value={formData.model_name}
          onChange={handleChange}
          placeholder="Select or type model name"
          required
          className="ai-character-form-input ai-character-form-input-monospace"
          list={`model-suggestions-${formData.model_provider}`}
        />
        <datalist id={`model-suggestions-${formData.model_provider}`}>
          {modelSuggestions[formData.model_provider]?.map((model) => (
            <option key={model} value={model} />
          ))}
        </datalist>
      </div>

      <div className="ai-character-form-group">
        <label htmlFor="persona_description">Persona Prompt</label>
        <textarea
          id="persona_description"
          name="persona_description"
          value={personaDescription}
          onChange={(e) => setPersonaDescription(e.target.value)}
          placeholder="Enter the character's persona prompt or description..."
          className="ai-character-form-textarea"
          rows={8}
        />
        <small style={{ color: '#666666', fontSize: '0.75rem' }}>
          This prompt defines the character's personality, behavior, and communication style.
        </small>
      </div>

      {/* Tool Selection - Only show for Claude provider */}
      {formData.model_provider === 'claude' && (
        <div className="ai-character-form-group">
          <label>Enabled Tools</label>
          <div className="ai-character-tools-selection">
            {availableTools.map((tool) => (
              <label key={tool.name} className="ai-character-tool-checkbox-label">
                <input
                  type="checkbox"
                  checked={enabledTools.includes(tool.name)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setEnabledTools([...enabledTools, tool.name]);
                    } else {
                      setEnabledTools(enabledTools.filter(t => t !== tool.name));
                    }
                  }}
                  className="ai-character-form-checkbox"
                />
                <span>{tool.label}</span>
              </label>
            ))}
          </div>
          <small style={{ color: '#666666', fontSize: '0.75rem' }}>
            Select which tools this character can use. If none are selected, the character will not have access to any tools.
          </small>
        </div>
      )}

      <div className="ai-character-form-group">
        <label className="ai-character-form-checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            className="ai-character-form-checkbox"
          />
          <span>Active</span>
        </label>
      </div>

      <div className="ai-character-form-actions">
        <button 
          type="button" 
          onClick={onCancel} 
          className="ai-character-form-btn ai-character-form-btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="ai-character-form-btn ai-character-form-btn-primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : character ? 'Update Character' : 'Create Character'}
        </button>
      </div>
    </form>
  );
};

export default AICharacterForm;

